<template>
  <div>
    <b-card>
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h4 class="mb-0">
          Journal d'actions
          <span v-if="userLabelText" class="text-primary">— {{ userLabelText }}</span>
        </h4>
        <b-button variant="outline-secondary" size="sm" :to="{ name: 'utilisateurs' }">
          <feather-icon icon="ArrowLeftIcon" />
          Retour
        </b-button>
      </div>

      <b-row>
        <b-col md="3">
          <b-form-group label="Date début">
            <flat-pickr
              v-model="filter.date_debut"
              :config="datePickerConfig"
              class="form-control"
              placeholder="JJ/MM/AAAA"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Date fin">
            <flat-pickr
              v-model="filter.date_fin"
              :config="datePickerConfig"
              class="form-control"
              placeholder="JJ/MM/AAAA"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Action">
            <v-select
              v-model="filter.action"
              :options="actionOptions"
              :reduce="o => o.value"
              label="label"
              placeholder="Toutes les actions"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Type d'entité">
            <v-select
              v-model="filter.subject_type"
              :options="subjectOptions"
              :reduce="o => o.value"
              label="label"
              placeholder="Toutes les entités"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="12" class="d-flex justify-content-end">
          <b-button variant="primary" class="mr-1" @click="reload(1)">
            <feather-icon icon="SearchIcon" />
            Rechercher
          </b-button>
          <b-button variant="outline-secondary" @click="resetFilters">
            <feather-icon icon="XIcon" />
            Réinitialiser
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <b-overlay :show="loading" rounded="sm">
      <b-card no-body>
        <div class="m-2 d-flex justify-content-between">
          <div class="align-self-center font-weight-bold">
            {{ pagination.total }} action(s) tracée(s)
          </div>
        </div>

        <b-table
          :fields="fields"
          :items="items"
          responsive
          show-empty
          empty-text="Aucune action enregistrée pour cet utilisateur."
          striped
          hover
          class="mb-0"
        >
          <template #cell(action)="data">
            <b-badge :variant="actionVariant(data.item.action)">
              {{ actionLabel(data.item.action) }}
            </b-badge>
          </template>

          <template #cell(subject)="data">
            <div>{{ subjectLabel(data.item.subject_type) }}</div>
            <small class="text-muted">#{{ data.item.subject_id }}</small>
          </template>

          <template #cell(details)="data">
            <div v-if="data.item.details && data.item.details.changes">
              <ul class="mb-0 pl-3 small">
                <li v-for="(diff, field) in data.item.details.changes" :key="field">
                  <strong>{{ field }}</strong> :
                  <span class="text-danger">{{ formatValue(diff.old) }}</span>
                  →
                  <span class="text-success">{{ formatValue(diff.new) }}</span>
                </li>
              </ul>
            </div>
            <span v-else class="text-muted small">—</span>
          </template>

          <template #cell(ip_address)="data">
            <small class="text-muted">{{ data.item.ip_address || '-' }}</small>
          </template>

          <template #cell(created_at)="data">
            <small>{{ formatDate(data.item.created_at) }}</small>
          </template>
        </b-table>

        <div class="m-2">
          <b-pagination
            v-model="pagination.current_page"
            :total-rows="pagination.total"
            :per-page="pagination.per_page"
            align="right"
            @change="reload"
          />
        </div>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormGroup,
  BButton,
  BTable,
  BBadge,
  BOverlay,
  BPagination,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import fr from 'flatpickr/dist/l10n/fr.js'
import axios from '@/libs/axios'

export default {
  name: 'UtilisateurJournal',
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BButton,
    BTable,
    BBadge,
    BOverlay,
    BPagination,
    vSelect,
    flatPickr,
  },
  data() {
    return {
      loading: false,
      items: [],
      userInfo: null,
      pagination: {
        current_page: 1,
        per_page: 25,
        total: 0,
      },
      filter: {
        date_debut: null,
        date_fin: null,
        action: null,
        subject_type: null,
      },
      datePickerConfig: {
        dateFormat: 'd/m/Y',
        locale: fr.fr,
        allowInput: true,
      },
      actionOptions: [
        { value: 'create', label: 'Création' },
        { value: 'update', label: 'Modification' },
        { value: 'delete', label: 'Suppression' },
      ],
      subjectOptions: [
        { value: 'App\\Models\\Vehicule', label: 'Véhicule' },
        { value: 'App\\Models\\Client', label: 'Client' },
        { value: 'App\\Models\\Offre', label: 'Offre' },
        { value: 'App\\Models\\User', label: 'Utilisateur' },
        { value: 'App\\Models\\Role', label: 'Rôle' },
      ],
      fields: [
        { key: 'created_at', label: 'Date' },
        { key: 'action', label: 'Action', class: 'text-center' },
        { key: 'subject', label: 'Entité' },
        { key: 'details', label: 'Détails' },
        { key: 'ip_address', label: 'IP' },
      ],
    }
  },
  computed: {
    userId() {
      return this.$route.params.id
    },
    userLabelText() {
      if (!this.userInfo) return ''
      const fullname = [this.userInfo.prenom, this.userInfo.nom].filter(Boolean).join(' ')
      return fullname || this.userInfo.email || `Utilisateur #${this.userInfo.id}`
    },
  },
  mounted() {
    this.reload(1)
  },
  methods: {
    async reload(page = 1) {
      this.loading = true
      try {
        const params = { page, per_page: this.pagination.per_page }
        Object.entries(this.filter).forEach(([k, v]) => {
          if (v !== null && v !== '' && v !== undefined) params[k] = v
        })
        const { data } = await axios.get(`/api/logs/utilisateur/${this.userId}`, { params })
        this.items = data.data || []
        this.pagination = {
          current_page: data.current_page || 1,
          per_page: data.per_page || this.pagination.per_page,
          total: data.total || 0,
        }
        if (this.items.length && this.items[0].user) {
          this.userInfo = this.items[0].user
        }
      } catch (e) {
        this.items = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    },
    resetFilters() {
      this.filter = {
        date_debut: null,
        date_fin: null,
        action: null,
        subject_type: null,
      }
      this.reload(1)
    },
    actionVariant(action) {
      switch (action) {
        case 'create': return 'primary'
        case 'update': return 'success'
        case 'delete': return 'danger'
        default: return 'secondary'
      }
    },
    actionLabel(action) {
      switch (action) {
        case 'create': return 'Création'
        case 'update': return 'Modification'
        case 'delete': return 'Suppression'
        default: return action
      }
    },
    subjectLabel(type) {
      const opt = this.subjectOptions.find(o => o.value === type)
      return opt ? opt.label : (type || '').split('\\').pop()
    },
    formatValue(v) {
      if (v === null || v === undefined || v === '') return '∅'
      if (typeof v === 'boolean') return v ? 'Oui' : 'Non'
      return String(v)
    },
    formatDate(value) {
      if (!value) return '-'
      const d = new Date(value)
      return d.toLocaleDateString('fr') + ' ' + d.toLocaleTimeString('fr', { hour: '2-digit', minute: '2-digit' })
    },
  },
}
</script>

<style lang="scss">
@import '~@core/scss/vue/libs/vue-select.scss';
@import '~@core/scss/vue/libs/vue-flatpicker.scss';
</style>
